const SEGMENT_KEY = 'mm-segment-v1';   // legacy single-segment key
const SEGMENTS_KEY = 'mm-segments-v2'; // current multi-segment key (array of ids)
const PROFILE_KEY = 'mm-profile-v1';

export const segments = {
  'uk-undergrad': {
    id: 'uk-undergrad',
    label: 'UK undergraduate',
    sub: 'Year 12 or 13 — UCAT route, first application',
    emoji: '🎓',
    welcome: "Let's sharpen the skills behind your UCAT score.",
    primaryTool: { to: '/practice', label: 'Today\'s drill' },
    showUcat: true,
    showGamsat: false,
    showInternational: false
  },
  'uk-gap': {
    id: 'uk-gap',
    label: 'Gap year / re-applicant',
    sub: 'Strengthening UCAT, work experience and application for the next cycle',
    emoji: '🔁',
    welcome: 'Re-application year — what changed since last cycle, and what to lean into now.',
    primaryTool: { to: '/strategy-audit', label: 'Strategy audit' },
    showUcat: true,
    showGamsat: false,
    showInternational: false,
    showReapply: true
  },
  'uk-graduate': {
    id: 'uk-graduate',
    label: 'Graduate-entry (GAMSAT)',
    sub: 'Already have a degree — applying via GAMSAT to GEM courses',
    emoji: '🩺',
    welcome: 'GAMSAT route — Section 3 is where 80% of marks live. Most UK GEM schools double-weight it.',
    primaryTool: { to: '/gamsat-essay', label: 'GAMSAT essay sprint' },
    showUcat: false,
    showGamsat: true,
    showInternational: false
  },
  'international': {
    id: 'international',
    label: 'International applicant',
    sub: 'Applying to UK medical schools from outside the UK',
    emoji: '🌍',
    welcome: 'International route — UCAT is still required for most UK med schools, plus extra rules on quotas, IELTS and fee status.',
    primaryTool: { to: '/where-can-i-apply', label: 'Where can I apply' },
    showUcat: true,
    showGamsat: false,
    showInternational: true
  }
};

export const disciplines = {
  medicine: { label: 'Medicine', emoji: '🩺', degree: 'MBBS / MBChB / BMBS' },
  dentistry: { label: 'Dentistry', emoji: '🦷', degree: 'BDS / BChD' }
};

export const roles = {
  student: { label: 'Student', emoji: '🎓' },
  parent: { label: 'Parent', emoji: '👪', path: '/parents' },
  teacher: { label: 'Teacher / advisor', emoji: '🧑‍🏫', path: '/teachers' }
};

export function getSegments() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SEGMENTS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter((id) => segments[id]).map((id) => segments[id]) : [];
    }
    // legacy single-segment fallback
    const legacy = localStorage.getItem(SEGMENT_KEY);
    if (legacy && segments[legacy]) return [segments[legacy]];
  } catch {}
  return [];
}

// Back-compat: first selected segment (callers using primary path).
export function getSegment() {
  const all = getSegments();
  return all.length > 0 ? all[0] : null;
}

export function setSegments(ids) {
  if (typeof window === 'undefined') return;
  try {
    const cleaned = Array.isArray(ids) ? ids.filter((id) => segments[id]) : [];
    if (cleaned.length > 0) localStorage.setItem(SEGMENTS_KEY, JSON.stringify(cleaned));
    else localStorage.removeItem(SEGMENTS_KEY);
    localStorage.removeItem(SEGMENT_KEY); // clear legacy key
  } catch {}
  try { window.dispatchEvent(new CustomEvent('mm-segments-change', { detail: ids })); } catch {}
}

// Back-compat shim.
export function setSegment(id) {
  setSegments(id ? [id] : []);
}

// Returns true if any chosen segment has the flag (e.g. showGamsat).
export function hasSegmentFlag(flag) {
  return getSegments().some((s) => s[flag]);
}

export function getProfile() {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || '{}'); } catch { return {}; }
}

export function setProfile(profile) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(profile)); } catch {}
  try { window.dispatchEvent(new CustomEvent('mm-profile-change', { detail: profile })); } catch {}
}

export function updateProfile(patch) {
  const current = getProfile();
  const next = { ...current, ...patch };
  setProfile(next);
  return next;
}

export function getDiscipline() {
  return getProfile().discipline || 'medicine';
}

export function setDiscipline(d) {
  updateProfile({ discipline: d });
}

// Discipline-aware copy. Components call this to get the right noun forms.
export function disciplineCopy(d = getDiscipline()) {
  if (d === 'dentistry') {
    return {
      field: 'dentistry', adj: 'dental', applicant: 'dental student',
      course: 'BDS', regulator: 'GDC', test: 'UCAT',
      role: 'dentist', plural: 'dentists',
      schoolType: 'dental school', schoolTypePlural: 'dental schools'
    };
  }
  return {
    field: 'medicine', adj: 'medical', applicant: 'medic',
    course: 'MBBS', regulator: 'GMC', test: 'UCAT',
    role: 'doctor', plural: 'doctors',
    schoolType: 'medical school', schoolTypePlural: 'medical schools'
  };
}
